import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { urls, reportingStandards } from '../../../constants';
import { ApplicationContext } from '../../../context/Application.context';
import ApplicationDescription from '../../ApplicationDescription';
import { isBoolean } from '../../../utils/validations';
import QuestionCard from '../../QuestionCard';
import Footnote from '../../Footnote';
import styles from './EligibilityStandards.module.scss';

const EligibilityStandards = ({ name }) => {
  const [state, dispatch] = useContext(ApplicationContext);
  const application = state.application;
  const isOTCQB = application.typeId === 4;

  const questions = [
    {
      title: 'Audited Financial Statements',
      subTitle: () => (
        <>
          <p>Does the company have audited annual financial statements that meet the criteria below?</p>
          <ul className={styles.criteriaList}>
            <li>US company financials must be audited by a PCAOB registered auditor and prepared in accordance to US GAAP.</li>
            <li>Non-US company financials must be prepared in accordance with US GAAP, IFRS or an IFRS equivalent standard.</li>
          </ul>
        </>
      ),
      fieldName: 'auditedFinancials',
      falseDetails: [
        {
          desc: 'Audited financial statements are required. Please explain when the company expects to have audited financials.',
          fieldName: 'auditedFinancialsDetail',
          appTypes: [1, 2, 3]
        },
        {
          desc: 'Audited financial statements are required. Please explain why the requirement described above is not currently met.',
          fieldName: 'auditedFinancialsDetail',
          appTypes: [4]
        }
      ],
      appTypes: [1, 2, 3, 4]
    },
    {
      title: 'Shell or a Blank Check Company',
      subTitle: () => <p>Is the company a <a href={urls.GLOSSARY_SHELL} target='_blank' rel='noopener noreferrer'>shell</a> or a blank check company?</p>,
      fieldName: 'isShellBlankCheckCompany',
      trueDetails: [
        {
          desc: 'Shell or Blank check companies do not qualify for OTCQX.  Please explain the company’s plan to meet this requirement.',
          fieldName: 'isShellBlankCheckCompanyDetail',
          appTypes: [1, 2, 3]
        }
      ],
      appTypes: [1, 2, 3, 4]
    },
    {
      title: 'In Good Standing',
      subTitle: 'Is the company in good standing under the laws of each jurisdiction in which the company is organized or does business?',
      fieldName: 'inGoodStanding',
      falseDetails: [
        {
          desc: 'Companies are required to be in good standing. Please explain the company’s plan to meet this requirement.',
          fieldName: 'inGoodStandingDetail',
          appTypes: [1, 2, 3, 4]
        }
      ],
      appTypes: [1, 2, 3, 4]
    },
    {
      title: 'Bankruptcy',
      subTitle: 'Is the company subject to any Bankruptcy or reorganization proceedings?',
      fieldName: 'isBankrupt',
      trueDetails: [
        {
          desc: 'Companies under bankruptcy or reorganization proceedings are not eligible for OTCQX. Please provide further information below on when your company plans to emerge out of bankruptcy or complete reorganization proceedings.',
          fieldName: 'isBankruptDetail',
          appTypes: [1, 2, 3]
        },
        {
          desc: 'Companies under bankruptcy or reorganization proceedings are not eligible for OTCQB. Please provide further information below on when your company plans to emerge out of bankruptcy or complete reorganization proceedings.',
          fieldName: 'isBankruptDetail',
          appTypes: [4]
        }
      ],
      appTypes: [1, 2, 3, 4]
    },
    {
      title: 'Independent Directors',
      subTitle: () => <>
        <p>Does the company have a board of directors that includes at least two (2) <a href={urls.GLOSSARY_IND_DIR} target='_blank' rel='noopener noreferrer'>Independent Directors</a>?</p>
        {isOTCQB && <Footnote text='Note: Required for Alternative Reporting Companies.' />}
      </>,
      fieldName: 'hasTwoIndependentDirectors',
      falseDetails: [
        {
          desc: () => <p>Companies are required to have at least two <a href={urls.GLOSSARY_IND_DIR} target='_blank' rel='noopener noreferrer'>Independent Directors</a> serve on the Board of Directors to qualify for OTCQX. Please explain the company’s plan to meet this requirement.</p>,
          fieldName: 'hasTwoIndependentDirectorsDetail',
          appTypes: [1, 2]
        },
        {
          desc: () => <p>Alternative Reporting Standard Companies are required to have at least one Independent Director upon application and may phase in the 2<sup>nd</sup> <a href={urls.GLOSSARY_IND_DIR} target='_blank' rel='noopener noreferrer'>Independent Director</a> within 90 days or the time of the Company’s next shareholder meeting. In any event, the independence requirement must be satisfied within one year of the Company joining OTCQB. Please explain the company’s plan to meet this requirement.</p>,
          fieldName: 'hasTwoIndependentDirectorsDetail',
          appTypes: [4]
        }
      ],
      appTypes: [1, 2, 4]
    },
    {
      title: 'Audit Committee',
      subTitle: () => <>
        <p>Does the company have an audit committee, a majority of whose members are <a href={urls.GLOSSARY_IND_DIR} target='_blank' rel='noopener noreferrer'>Independent Directors</a>?</p>
        {isOTCQB && <Footnote text='Note: Required for Alternative Reporting Companies.' />}
      </>,
      fieldName: 'hasAuditComittee',
      falseDetails: [
        {
          desc: () => <p>Companies are required to have an audit committee with a majority of them being <a href={urls.GLOSSARY_IND_DIR} target='_blank' rel='noopener noreferrer'>Independent Directors</a>. Please explain the company’s plan to meet this requirement.</p>,
          fieldName: 'hasAuditComitteeDetail',
          appTypes: [1, 2]
        },
        {
          desc: () => <p>Alternative Reporting Standard Companies are required to have an audit committee with at least 1 Independent director upon application and may phase in the 2<sup>nd</sup> <a href={urls.GLOSSARY_IND_DIR} target='_blank' rel='noopener noreferrer'>Independent Director</a> within 90 days or the time of the Company’s next shareholder meeting. Please explain the company’s plan to meet this requirement..</p>,
          fieldName: 'hasAuditComitteeDetail',
          appTypes: [4]
        }
      ],
      appTypes: [1, 2, 4]
    },
    {
      title: `Annual Shareholders' Meetings`,
      subTitle: `Does the company conduct annual shareholders' meetings and make annual financial reports available to its shareholders at least 15 calendar days prior to such meetings?`,
      fieldName: 'hasShareholderMeetings',
      falseDetails: [
        {
          desc: 'Please explain why the company currently does not hold Annual Shareholder’s Meetings and provide information on whether the company plans to hold such meetings once it joins OTCQX.',
          fieldName: 'hasShareholderMeetingsDetail',
          appTypes: [1, 2]
        }
      ],
      appTypes: [1, 2]
    }
  ];

  const eligibilityStandards = application.eligibilityStandards = { ...application.eligibilityStandards };
  const applicationQuestions = questions.filter(q => q.appTypes.includes(application.typeId));
  const appTypeName = application.typeDesc;
  const isReadOnly = state.readOnly;
  const showIncompletes = state.showIncompletes;

  const onUpdate = data => {
    dispatch({
      type: 'UPDATE_APPLICATION',
      payload: data,
      appField: 'eligibilityStandards',
      card: 'eligibility-standards'
    });
  };

  const onValueChanged = (value, field) => {
    const updateEligibilityStandards = { ...eligibilityStandards };
    updateEligibilityStandards[field] = value;
    onUpdate(updateEligibilityStandards);
  };

  const handleShowDetail = detailObject => {
    const showDetail = detailObject.appTypes.includes(application.typeId);
    if (isOTCQB && (detailObject.fieldName === 'hasTwoIndependentDirectorsDetail' || detailObject.fieldName === 'hasAuditComitteeDetail')) {
      return showDetail && application.reportingStandard === reportingStandards.ALTERNATIVE;
    }

    return showDetail;
  };

  const handleQuestionClick = (value, field) => {
    const updateEligibilityStandards = { ...eligibilityStandards };
    updateEligibilityStandards[field] = value;
    onUpdate(updateEligibilityStandards);
  };

  const renderQuestons = () => {
    return applicationQuestions.map((q, i) => {
      return <QuestionCard
        key={i}
        item={q}
        onShowDetail={handleShowDetail}
        readOnly={isReadOnly}
        section={eligibilityStandards}
        isValid={showIncompletes ? isBoolean(eligibilityStandards && eligibilityStandards[q.fieldName]) : true}
        showIncompletes={showIncompletes}
        handleQuestionClick={handleQuestionClick}
        onValueChanged={onValueChanged} />;
    });
  };

  return (
    <div>
      <ApplicationDescription
        title={name}
        desc={`To be eligible for ${appTypeName}, a Company must meet the following standards.`} />
      {renderQuestons()}
    </div>
  );
};

EligibilityStandards.propTypes = {
  name: PropTypes.string
};

export default EligibilityStandards;
